import React, { useState, useEffect } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import Button from '@material-ui/core/Button'
import Chip from '@material-ui/core/Chip'
import Card from '@material-ui/core/Card'
import CardHeader from '@material-ui/core/CardHeader'
import Typography from '@material-ui/core/Typography'
import BootstrapTable from 'react-bootstrap-table-next'
import Grid from '@material-ui/core/Grid'
import Loader from 'components/general/Loader'
import filterFactory from 'react-bootstrap-table2-filter'
import AddEnergyDeliveryModal from './AddEnergyDeliveryModal'
import EnergyDeliveryModal from './EnergyDeliveryModal'

import componentStyles from 'assets/theme/views/admin/react-bs-table.js'

const useStyles = makeStyles(componentStyles)

function EnergyDeliveriesTable(props) {
    const classes = { ...useStyles() }
    const isThisWeek = props.period === 'thisWeek'
    const isAllProducts = props.product === 'all'

    // Build a map of companyId → coverageType from companies prop
    const companyMap = {}
    if (props.companies) {
        for (const c of props.companies) {
            companyMap[c.companyId] = c
        }
    }

    const [deliveries, setDeliveries] = useState(props.deliveries)
    const [delivery, setDelivery] = useState(null)
    const [showDeliveryModal, setShowDeliveryModal] = useState(false)
    const [showAddDeliveryModal, setShowAddDeliveryModal] = useState(false)

    useEffect(() => {
        setDeliveries(props.deliveries)
    }, [props.deliveries])

    const columns = [
        {
            dataField: 'companyName',
            text: 'Company',
            sort: true,
            editable: false,
            formatter: (cell, row) => {
                return (
                    <Box 
                        className={row.active === false ? 'inactive-delivery hover-text primary-text' : 'active-delivery hover-text primary-text'} 
                        onClick={() => openDeliveryModal(row)}
                        style={{ cursor: 'pointer' }}
                    >
                        {row.companyName}
                        {row.priority === true && (
                            <Chip
                                label="Priority"
                                size="small"
                                style={{ marginLeft: '8px', backgroundColor: '#f5365c', color: '#fff', fontWeight: 600, fontSize: '0.65rem', height: '20px' }}
                            />
                        )}
                        {row.businessHoursOnly === true && (
                            <Chip
                                label="Business hours"
                                size="small"
                                style={{ marginLeft: '8px', backgroundColor: '#f5365c', color: '#fff', fontWeight: 600, fontSize: '0.65rem', height: '20px' }}
                            />
                        )}
                        {row.weekendLeads === false && row.businessHoursOnly !== true && (
                            <Chip
                                label="No weekend"
                                size="small"
                                style={{ marginLeft: '8px', backgroundColor: '#f5365c', color: '#fff', fontWeight: 600, fontSize: '0.65rem', height: '20px' }}
                            />
                        )}
                        {row.billingCompanyId && row.billingCompanyId !== row.companyId && (
                            <Chip
                                label={`Billed to ${(companyMap[row.billingCompanyId] && companyMap[row.billingCompanyId].name) || row.billingCompanyId}`}
                                size="small"
                                style={{ marginLeft: '8px', backgroundColor: '#11cdef', color: '#fff', fontWeight: 600, fontSize: '0.65rem', height: '20px' }}
                            />
                        )}
                    </Box>
                )
            },
            footer: 'Total'
        },
        ...(isAllProducts ? [{
            dataField: 'product',
            text: 'Product',
            sort: true,
            editable: false,
            formatter: (cell, row) => {
                return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>{row.product}</Box>
            },
            footer: ''
        }] : []),
        {
            dataField: 'coverageType',
            text: 'Coverage',
            sort: true,
            editable: false,
            isDummyField: true,
            formatter: (cell, row) => {
                const company = companyMap[row.companyId]
                const type = company && company.coverageType === 'eircodes' ? 'Eircode' : 'County'
                const color = type === 'Eircode' ? '#5e72e4' : '#8898aa'
                return (
                    <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>
                        <span style={{ fontSize: '12px', fontWeight: 600, color }}>{type}</span>
                    </Box>
                )
            },
            footer: ''
        },
        {
            dataField: 'billingMode',
            text: 'Billing',
            sort: true,
            editable: false,
            formatter: (cell, row) => {
                const mode = row.billingMode === 'cap' ? 'Cap' : 'Actual'
                const color = mode === 'Cap' ? '#5e72e4' : '#8898aa'
                return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>
                    <span style={{ fontSize: '12px', fontWeight: 600, color }}>{mode}</span>
                </Box>
            },
            footer: ''
        },
        {
            dataField: 'leadPrice',
            text: 'Lead Price',
            sort: true,
            editable: false,
            formatter: (cell, row) => {
                return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>€{row.leadPrice ? row.leadPrice.toFixed(2) : '0.00'}</Box>
            },
            footer: ''
        },
        {
            dataField: 'weeklyCap',
            text: 'Cap (Effective)',
            sort: true,
            editable: false,
            formatter: (cell, row) => {
                const scheduled = row.scheduledWeeklyCap != null
                    ? <span title="Scheduled — applies after Saturday's cron" style={{ fontSize: '11px', color: '#5e72e4', fontWeight: 600, marginLeft: '4px' }}>→ {row.scheduledWeeklyCap}</span>
                    : null
                if (row.weeklyCap == null) {
                    return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>∞{scheduled}</Box>
                }
                const effective = Math.max(0, row.weeklyCap - (row.surgeAdjustment || 0))
                const showEffective = (row.surgeAdjustment || 0) !== 0
                return (
                    <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>
                        {row.weeklyCap}
                        {showEffective && <span style={{ fontSize: '11px', color: '#8898aa', marginLeft: '4px' }}>({effective})</span>}
                        {scheduled}
                    </Box>
                )
            },
            footer: (() => {
                const total = deliveries.reduce((sum, d) => sum + (d.weeklyCap || 0), 0)
                const effectiveTotal = deliveries.reduce((sum, d) => sum + Math.max(0, (d.weeklyCap || 0) - (d.surgeAdjustment || 0)), 0)
                const hasUnlimited = deliveries.some(d => d.weeklyCap == null)
                const base = hasUnlimited ? total + ' + ∞' : String(total)
                return effectiveTotal !== total ? `${base} (${effectiveTotal})` : base
            })()
        },
        {
            dataField: 'minEELeads',
            text: 'Min EE',
            sort: true,
            editable: false,
            formatter: (cell, row) => {
                if (row.minEELeads == null) {
                    return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>—</Box>
                }
                const mode = row.eeLeadMode || 'mixed'
                const modeLabel = mode === 'first' ? 'First' : 'Mixed'
                const modeColor = mode === 'first' ? '#5e72e4' : '#8898aa'
                return (
                    <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>
                        {row.minEELeads}{' '}
                        <span style={{ fontSize: '11px', color: modeColor, fontWeight: 600 }}>({modeLabel})</span>
                    </Box>
                )
            },
            footer: (() => {
                const total = deliveries.reduce((sum, d) => sum + (d.minEELeads || 0), 0)
                return total > 0 ? String(total) : ''
            })()
        },
        {
            dataField: 'surgeEnabled',
            text: 'Surge',
            sort: true,
            editable: false,
            formatter: (cell, row) => {
                if (!row.surgeEnabled) {
                    return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>—</Box>
                }
                const bal = row.surgeBalance || 0
                const balColor = bal > 0 ? '#f5365c' : bal < 0 ? '#2dce89' : '#8898aa'
                return (
                    <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>
                        <span style={{ color: '#5e72e4', fontWeight: 600 }}>±{row.surgePercent || 0}%</span>
                        {' '}
                        <span style={{ fontSize: '11px', color: balColor, fontWeight: 600 }}>
                            ({bal > 0 ? '+' : ''}{bal})
                        </span>
                    </Box>
                )
            },
            footer: ''
        },
        {
            dataField: 'leadCount',
            text: 'Leads',
            sort: true,
            editable: false,
            formatter: (cell, row) => {
                return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>{row.leadCount || 0}</Box>
            },
            footer: deliveries.reduce((total, d) => total + (parseInt(d.leadCount) || 0), 0).toString()
        },
        ...(isThisWeek ? [{
            dataField: 'remaining',
            text: 'Remaining',
            sort: true,
            editable: false,
            isDummyField: true,
            formatter: (cell, row) => {
                const company = companyMap[row.companyId]
                const isEircode = company && company.coverageType === 'eircodes'
                if (isEircode || row.weeklyCap == null) {
                    return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>—</Box>
                }
                const effectiveCap = Math.max(0, (row.weeklyCap || 0) - (row.surgeAdjustment || 0))
                const remaining = Math.max(0, effectiveCap - (row.leadCount || 0))
                return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>{remaining}</Box>
            },
            footer: (() => {
                const total = deliveries.reduce((sum, d) => {
                    const company = companyMap[d.companyId]
                    const isEircode = company && company.coverageType === 'eircodes'
                    if (isEircode || d.weeklyCap == null) return sum
                    const effectiveCap = Math.max(0, (d.weeklyCap || 0) - (d.surgeAdjustment || 0))
                    return sum + Math.max(0, effectiveCap - (d.leadCount || 0))
                }, 0)
                return String(total)
            })()
        },
        {
            dataField: 'fillRate',
            text: 'Fill %',
            sort: true,
            editable: false,
            formatter: (cell, row) => {
                if (row.weeklyCap == null) {
                    return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>—</Box>
                }
                const fill = row.weeklyCap > 0 ? Math.round((row.leadCount || 0) / row.weeklyCap * 100) : 0
                let color = '#2dce89' // green
                if (fill >= 90) color = '#f5365c' // red — near cap
                else if (fill >= 70) color = '#fb6340' // orange
                else if (fill >= 50) color = '#ffd600' // yellow

                return (
                    <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>
                        <Box display='inline-flex' alignItems='center' style={{ gap: '6px' }}>
                            <Box style={{ 
                                width: '50px', height: '8px', borderRadius: '4px', 
                                backgroundColor: '#e9ecef', overflow: 'hidden' 
                            }}>
                                <Box style={{ 
                                    width: `${Math.min(fill, 100)}%`, height: '100%', 
                                    borderRadius: '4px', backgroundColor: color 
                                }} />
                            </Box>
                            <span>{fill}%</span>
                        </Box>
                    </Box>
                )
            },
            footer: ''
        }] : []),
        {
            dataField: 'revenue',
            text: 'Revenue',
            sort: true,
            editable: false,
            formatter: (cell, row) => {
                const leads = row.leadCount || 0
                const price = row.leadPrice || 0
                let rev
                if (row.billingMode === 'cap' && row.weeklyCap != null) {
                    rev = Math.min(leads, row.weeklyCap) * price
                } else {
                    rev = leads * price
                }
                return <Box className={row.active === false ? 'inactive-delivery' : 'active-delivery'}>€{rev.toFixed(2)}</Box>
            },
            footer: '€' + deliveries.reduce((total, d) => {
                const leads = d.leadCount || 0
                const price = d.leadPrice || 0
                if (d.billingMode === 'cap' && d.weeklyCap != null) {
                    return total + Math.min(leads, d.weeklyCap) * price
                }
                return total + leads * price
            }, 0).toFixed(2)
        }
    ]

    function openDeliveryModal(delivery) {
        // Expanded (source-scoped) display rows carry a reference to the single
        // underlying record; always edit that so the one modal manages base +
        // all source overrides together.
        setDelivery(delivery.__baseDelivery || delivery)
        setShowDeliveryModal(true)
    }

    function closeDeliveryModal() {
        setShowDeliveryModal(false)
        setDelivery(null)
    }

    return (
        <Card>
            <CardHeader
                subheader={
                    <Box display='flex' style={{justifyContent: 'space-between'}}>
                        <Box>
                            <Box
                                component={Typography}
                                variant='h2'
                                marginBottom='0!important'
                            >
                                <Box component='span'>{props.title}</Box>
                            </Box>
                        </Box>
                        <Box>
                            <Button
                                component={Box}
                                onClick={() => setShowAddDeliveryModal(true)}
                                color='primary'
                                variant='contained'
                                size='small'
                            >
                                Add Delivery
                            </Button>
                        </Box>
                    </Box>
                }
                classes={{ root: classes.cardHeaderRoot }}
            />
            
            <div className='table-container'>
                <BootstrapTable 
                    keyField='compositeKey' 
                    data={ deliveries } 
                    columns={ columns } 
                    filter={ filterFactory() } 
                    noDataIndication={<Box>No deliveries</Box>}
                />
            </div>

            {props.showRerunTargetsButton && (
                <Box
                    style={{
                        padding: '0.9rem 1.5rem 1.1rem',
                        borderTop: '1px solid #e9ecef',
                        backgroundColor: '#fafbfc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '1rem'
                    }}
                >
                    <Typography
                        variant='body2'
                        style={{
                            fontSize: '12px',
                            color: '#8898aa',
                            lineHeight: 1.6,
                            paddingRight: '1rem'
                        }}
                    >
                        Re-run targets after any Solar delivery is updated, activated, or deactivated so allocation targets stay in sync.
                    </Typography>
                    <Button
                        component={Box}
                        onClick={props.onRerunTargets}
                        color='default'
                        variant='outlined'
                        size='small'
                        disabled={props.rerunningTargets}
                        style={{ flexShrink: 0 }}
                    >
                        {props.rerunningTargets ? 'Recalculating Solar Targets...' : 'Recalculate Solar Targets'}
                    </Button>
                </Box>
            )}

            {showAddDeliveryModal &&
                <AddEnergyDeliveryModal 
                    isOpen={showAddDeliveryModal}
                    close={() => setShowAddDeliveryModal(false)}
                    showSuccessAlert={(text) => props.showSuccessAlert(text)}
                    refreshDeliveries={() => props.refreshDeliveries()}
                    companies={props.companies}
                    deliveries={deliveries}
                />
            }

            {showDeliveryModal &&
                <EnergyDeliveryModal 
                    isOpen={showDeliveryModal}
                    close={() => closeDeliveryModal()}
                    delivery={delivery}
                    deliveries={deliveries}
                    updateDelivery={(delivery) => props.updateDelivery(delivery)}
                    showSuccessAlert={(text) => props.showSuccessAlert(text)}
                    companies={props.companies}
                />
            }
        </Card>
    )
}

export default EnergyDeliveriesTable
